import {
  Box,
  Divider,
  Flex,
  PrimaryButton,
  LegacyText,
  TransparentButton,
} from "flicket-ui";
import { useState } from "react";
import { useTheme } from "styled-components";

import { getLightOrDarkColor } from "~lib/helpers";

import { Icon, Select } from "~components/common";
import { seatSelectTypeOptions } from "~components/reservation/constants";
import {
  AlcoholFreeZone,
  CoverType,
  SortBy,
} from "~components/reservation/options";
import {
  OptionKey,
  useReservationStore,
} from "~features/seated-reservation/store";
import * as actions from "~features/seated-reservation/store/actions/index";
import { useFilterOptions } from "~hooks/useFilterOptions";
import { useSeekaConverge } from "@seeka-labs/converge-react";

interface MobileFiltersProps {
  amountDisabled: boolean;
  seatAmountOptions: {
    value: number;
    label: string;
  }[];
  filterOptions?: string[];
}

export const MobileFilters = ({
  amountDisabled,
  seatAmountOptions,
  filterOptions,
}: MobileFiltersProps) => {
  const converge = useSeekaConverge();
  const theme = useTheme();
  const [showFilters, setShowFilters] = useState(false);
  const toggleFilters = () => setShowFilters(!showFilters);

  const options = useReservationStore((state) => state.options);

  const updateOptions = async (key: OptionKey, value: any) => {
    await actions.updateOptions({ options: { [key]: value } }, converge);
  };

  const { hasOption } = useFilterOptions(filterOptions);

  return (
    <>
      <Box
        d={{ _: "flex", md: "none" }}
        py={1}
        px={2}
        justifyContent="space-between"
        borderBottom="1px solid"
        borderBottomColor="N200"
        borderTop="1px solid"
        borderTopColor="N200"
        backgroundColor={"white"}
      >
        <Box minW="104px" borderRight="1px solid" borderColor="N200" pr={1}>
          <Select
            value={seatAmountOptions.find(
              (option) => option.value === options.amount
            )}
            options={seatAmountOptions}
            onChange={async (e) => updateOptions("amount", e.value)}
            hideSelectedOptions
            isDisabled={amountDisabled}
            small
          />
        </Box>
        <Flex alignItems="center">
          <LegacyText mr={1} fontSize={1} fontWeight="medium">
            Show all seat types
          </LegacyText>
          <PrimaryButton
            bg="P200"
            color={getLightOrDarkColor({
              background: theme.colors.P200,
              dark: theme.colors.N800,
            })}
            onClick={toggleFilters}
            size="sm"
          >
            <Icon icon="filter" fontSize={4} mr="1/4" />
            <LegacyText fontSize={1} fontWeight="medium">
              Filter
            </LegacyText>
          </PrimaryButton>
        </Flex>
      </Box>
      {showFilters && (
        <Box
          position="fixed"
          top={0}
          left={0}
          width="100vw"
          height="100%"
          maxHeight="100vh"
          overflowY="auto"
          bg="white"
          zIndex="popover"
          px={2}
          py={3}
        >
          <Flex justifyContent="space-between" mb={3}>
            <LegacyText fontSize={6} fontWeight="extraBold" color="N800">
              Filter options
            </LegacyText>
            <TransparentButton onClick={toggleFilters}>
              <Icon icon="close" fontSize={6} />
            </TransparentButton>
          </Flex>
          <Divider />
          <Box w="100%" alignItems="center" zIndex="popover" mt={3}>
            <LegacyText
              fontSize={2}
              color="N800"
              fontWeight="extraBold"
              mr={2}
              whiteSpace="nowrap"
              mb="6/4"
              mt={3}
            >
              I need tickets for
            </LegacyText>
            <Select
              value={seatAmountOptions.find(
                (opt) => opt.value === options.amount
              )}
              options={seatAmountOptions}
              onChange={async (e) => updateOptions("amount", e.value)}
              hideSelectedOptions
            />

            {hasOption("cover") && (
              <>
                <LegacyText
                  fontSize={2}
                  color="N800"
                  fontWeight="extraBold"
                  whiteSpace="nowrap"
                  mb="6/4"
                  mt={3}
                >
                  Seat cover
                </LegacyText>
                <CoverType
                  changeCoverType={async (value) =>
                    updateOptions("coverType", value)
                  }
                  selectedType={options.coverType}
                />
              </>
            )}

            {hasOption("alcohol") && (
              <>
                <LegacyText
                  fontSize={2}
                  color="N800"
                  fontWeight="extraBold"
                  whiteSpace="nowrap"
                  mb="6/4"
                  mt={3}
                >
                  Alcohol free zone
                </LegacyText>
                <AlcoholFreeZone
                  alcoholFreeZone={options.zoneType}
                  changeAlcoholFreeZone={async (value) =>
                    updateOptions("zoneType", value)
                  }
                />
              </>
            )}

            <LegacyText
              fontSize={2}
              color="N800"
              fontWeight="extraBold"
              whiteSpace="nowrap"
              mr={1}
              my={3}
            >
              Sort by
            </LegacyText>
            <SortBy
              selectedSort={options.selectedSort}
              changeSort={async (value) => updateOptions("selectedSort", value)}
            />

            <LegacyText
              fontSize={2}
              color="N800"
              fontWeight="extraBold"
              whiteSpace="nowrap"
              mr={1}
              my={3}
            >
              Seat selection
            </LegacyText>
            <Select
              value={seatSelectTypeOptions.find(
                (opt) => opt.value === options.seatSelectType
              )}
              options={seatSelectTypeOptions}
              onChange={async (e) => updateOptions("seatSelectType", e.value)}
              hideSelectedOptions
            />
          </Box>
          <Box mt={3}>
            <Divider />
            <PrimaryButton mt={3} onClick={toggleFilters}>
              Set Filter
            </PrimaryButton>
          </Box>
        </Box>
      )}
    </>
  );
};
